import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Printer, Trash, CheckCircle, MagnifyingGlass, PencilSimple, Eye } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const uid = () => Math.random().toString(36).slice(2, 10);
const emptyItem = () => ({ uid: uid(), source: "dpi", ppe_id: "", tool_id: "", description: "", quantity: 1, size: "", replacement: "Ad usura", kind: "D.P.I." });
const toForm = (d) => ({
  id: d.id, employee_id: d.employee_id, date: d.date, place: d.place || "", notes: d.notes || "",
  progressive: d.progressive,
  items: (d.items || []).map((i) => ({
    uid: uid(),
    source: i.ppe_id ? "dpi" : i.tool_id ? "tool" : "free",
    ppe_id: i.ppe_id || "", tool_id: i.tool_id || "", description: i.description || "",
    quantity: i.quantity ?? 1, size: i.size || "", replacement: i.replacement || "Ad usura",
    kind: i.kind || "D.P.I.",
  })),
});

export const PpeDeliveries = () => {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [ppeList, setPpeList] = useState([]);
  const [tools, setTools] = useState([]);
  const [signed, setSigned] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [batch, setBatch] = useState(null);

  useEffect(() => {
    if (!batch) return;
    api.get("/safety/deliveries/print-batch/count",
      { params: { day: batch.day, only_unsigned: batch.onlyUnsigned } })
      .then((r) => setBatch((b) => (b ? { ...b, count: r.data.count } : b)))
      .catch(() => {});
  }, [batch?.day, batch?.onlyUnsigned]); // eslint-disable-line react-hooks/exhaustive-deps

  const printBatch = async () => {
    try {
      await download(`/safety/deliveries/print-batch?day=${batch.day}&only_unsigned=${batch.onlyUnsigned}`,
        `moduli_dpi_${batch.day}.pdf`);
      setBatch(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(async () => {
    try {
      setRows((await api.get("/safety/deliveries", { params: { signed } })).data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [signed]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/safety/ppe").then((r) => setPpeList(r.data)).catch(() => {});
    api.get("/safety/tools").then((r) => setTools(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const items = (form.items || []).filter((i) => i.ppe_id || i.tool_id || i.description).map((i) => ({
        ppe_id: i.source === "dpi" ? i.ppe_id || null : null,
        tool_id: i.source === "tool" ? i.tool_id || null : null,
        description: i.description || "",
        quantity: Number(i.quantity || 1), size: i.size || "",
        replacement: i.replacement || "Ad usura",
        kind: i.source === "tool" ? "ATTREZZATURA" : i.source === "free" ? (i.kind || "D.P.I.") : "D.P.I.",
      }));
      const payload = {
        employee_id: form.employee_id, date: form.date, place: form.place || "", items,
        notes: form.notes || "",
      };
      if (form.id) {
        await api.put(`/safety/deliveries/${form.id}`, payload);
        toast.success(`Consegna n. ${form.progressive} aggiornata`);
        setForm(null);
        load();
        return;
      }
      const r = await api.post("/safety/deliveries", payload);
      if (r.data.registro === "attrezzature") {
        toast.success(`${r.data.consegne.length} attrezzature registrate nel Registro Consegna Attrezzature`);
        setForm(null);
        load();
        return;
      }
      toast.success(`Consegna n. ${r.data.progressive} registrata: stampa il modulo e fallo firmare`);
      setForm(null);
      load();
      download(`/safety/deliveries/${r.data.id}/pdf`, `consegna_dpi_${r.data.progressive}.pdf`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/safety/deliveries/${toDelete.id}`);
      toast.success(`Consegna n. ${toDelete.progressive} eliminata`);
      setToDelete(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const visible = rows.filter((r) => !q || `${r.employee_name} ${r.progressive}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="safety-deliveries-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Registro Consegna DPI</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ogni consegna genera il modulo da far firmare al dipendente e da conservare nell'archivio cartaceo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-sm font-semibold"
            data-testid="deliveries-print-unsigned"
            onClick={() => download("/safety/deliveries/print-unsigned", "moduli_dpi_da_firmare.pdf")
              .catch((e) => toast.error(errMsg(e)))}>
            <Printer size={15} className="mr-1.5" /> Stampa DA FIRMARE
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm font-semibold" data-testid="deliveries-print-batch"
            onClick={() => setBatch({ day: today(), onlyUnsigned: true, count: null })}>
            <Printer size={15} className="mr-1.5" /> Stampa moduli del giorno
          </Button>
          {canEdit && (
            <Button size="sm" className="rounded-sm font-semibold" data-testid="delivery-add"
              onClick={() => setForm({ date: today(), items: [emptyItem()] })}>
              <Plus size={15} className="mr-1.5" /> Nuova consegna</Button>
          )}
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dipendente o numero..."
            className="h-9 w-60 pl-8 rounded-sm" data-testid="delivery-search" />
        </div>
        <Select value={signed} onValueChange={setSigned}>
          <SelectTrigger className="h-9 w-[200px] rounded-sm" data-testid="delivery-signed-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le consegne</SelectItem>
            <SelectItem value="no">Da firmare</SelectItem>
            <SelectItem value="yes">Firmate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm tbl-sticky" data-testid="deliveries-table">
          <thead className="bg-secondary"><tr>
            {["", "N.", "Data", "Dipendente", "Articoli", "Firmato"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {visible.map((d) => (
              <tr key={d.id} className="border-t border-border hover:bg-secondary/40" data-testid={`delivery-row-${d.progressive}`}>
                <td className="px-3 py-1.5 text-left whitespace-nowrap">
                  <div className="inline-flex items-center gap-2">
                    <button className="text-muted-foreground transition-colors hover:text-primary" title="Consulta dettaglio"
                      data-testid={`delivery-view-${d.progressive}`} onClick={() => setView(d)}>
                      <Eye size={16} />
                    </button>
                    {canEdit && (
                      <button className="text-muted-foreground transition-colors hover:text-primary" title="Modifica consegna"
                        data-testid={`delivery-edit-${d.progressive}`} onClick={() => setForm(toForm(d))}>
                        <PencilSimple size={16} />
                      </button>
                    )}
                    <button className="text-muted-foreground transition-colors hover:text-primary" title="Stampa modulo"
                      data-testid={`delivery-print-${d.progressive}`}
                      onClick={() => download(`/safety/deliveries/${d.id}/pdf`, `consegna_dpi_${d.progressive}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                      <Printer size={16} />
                    </button>
                    {canEdit && (
                      <button className="text-muted-foreground transition-colors hover:text-rose-500" title="Elimina consegna"
                        data-testid={`delivery-delete-${d.progressive}`} onClick={() => setToDelete(d)}>
                        <Trash size={16} />
                      </button>
                    )}
                    {canEdit && !d.signed && (
                      <button className="text-xs font-semibold text-emerald-600 hover:underline"
                        data-testid={`delivery-sign-${d.progressive}`}
                        onClick={async () => {
                          try { await api.patch(`/safety/deliveries/${d.id}`, { signed: true }); toast.success("Consegna firmata e archiviata"); load(); }
                          catch (e) { toast.error(errMsg(e)); }
                        }}>
                        Segna firmata
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 font-semibold tabular-nums">{d.progressive}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{itDate(d.date)}</td>
                <td className="px-3 py-1.5">{d.employee_name}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">
                  {(d.items || []).map((i) => `${i.description} x${i.quantity}`).join(" · ")}
                </td>
                <td className="px-3 py-1.5">
                  {d.signed
                    ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle size={14} weight="fill" /> {itDate(d.signed_date)}</span>
                    : <span className="text-xs text-amber-600 font-semibold">DA FIRMARE</span>}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground" data-testid="deliveries-empty">Nessuna consegna registrata</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="delivery-dialog">
            <DialogHeader><DialogTitle className="font-head">
              {form.id ? `Modifica consegna n. ${form.progressive}`
                       : "Nuova consegna DPI / attrezzi da lavoro"}
            </DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dipendente</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="delivery-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Data consegna</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="delivery-date" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Luogo (per la data in calce)</Label>
                <Input value={form.place ?? ""} onChange={(e) => setForm({ ...form, place: e.target.value })}
                  placeholder="Corigliano-Rossano (CS)" className="h-9 rounded-sm mt-1" data-testid="delivery-place" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
                <Input value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="delivery-notes" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Articoli consegnati</div>
                <Button size="sm" variant="outline" className="rounded-sm" data-testid="delivery-add-item"
                  onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}>
                  <Plus size={14} className="mr-1" /> Aggiungi riga
                </Button>
              </div>
              {form.items.map((it, idx) => (
                <div key={it.uid} className="grid sm:grid-cols-12 gap-2 items-end border border-border rounded-sm p-2"
                  data-testid={`delivery-item-${idx}`}>
                  <div className="sm:col-span-3">
                    <Label className="text-[10px] uppercase text-muted-foreground">Tipo</Label>
                    <Select value={it.source}
                      onValueChange={(v) => {
                        const items = [...form.items];
                        items[idx] = { ...emptyItem(), uid: it.uid, source: v, quantity: it.quantity };
                        setForm({ ...form, items });
                      }}>
                      <SelectTrigger className="h-9 rounded-sm mt-1" data-testid={`item-source-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dpi">DPI in magazzino</SelectItem>
                        <SelectItem value="tool">Attrezzo da lavoro</SelectItem>
                        <SelectItem value="free">Descrizione libera</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-4">
                    <Label className="text-[10px] uppercase text-muted-foreground">Articolo</Label>
                    {it.source === "dpi" && (
                      <Select value={it.ppe_id}
                        onValueChange={(v) => {
                          const items = [...form.items];
                          const p = ppeList.find((x) => x.id === v);
                          items[idx] = { ...it, ppe_id: v, description: p?.name || "", size: p?.size || it.size };
                          setForm({ ...form, items });
                        }}>
                        <SelectTrigger className="h-9 rounded-sm mt-1" data-testid={`item-ppe-${idx}`}><SelectValue placeholder="Seleziona DPI" /></SelectTrigger>
                        <SelectContent className="max-h-56">
                          {ppeList.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {it.source === "tool" && (
                      <Select value={it.tool_id}
                        onValueChange={(v) => {
                          const items = [...form.items];
                          const t = tools.find((x) => x.id === v);
                          items[idx] = { ...it, tool_id: v, description: t?.name || "", size: t?.size || it.size };
                          setForm({ ...form, items });
                        }}>
                        <SelectTrigger className="h-9 rounded-sm mt-1" data-testid={`item-tool-${idx}`}><SelectValue placeholder="Coltello, acciaino, gancio..." /></SelectTrigger>
                        <SelectContent className="max-h-56">
                          {tools.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {it.source === "free" && (
                      <Input value={it.description}
                        onChange={(e) => {
                          const items = [...form.items];
                          items[idx] = { ...it, description: e.target.value };
                          setForm({ ...form, items });
                        }}
                        placeholder="Descrizione articolo" className="h-9 rounded-sm mt-1" data-testid={`item-desc-${idx}`} />
                    )}
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Q.tà</Label>
                    <Input value={it.quantity}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, quantity: e.target.value };
                        setForm({ ...form, items });
                      }} className="h-9 rounded-sm mt-1" data-testid={`item-qty-${idx}`} />
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Taglia</Label>
                    <Input value={it.size}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, size: e.target.value };
                        setForm({ ...form, items });
                      }} className="h-9 rounded-sm mt-1" data-testid={`item-size-${idx}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Sostituzione</Label>
                    <Input value={it.replacement}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, replacement: e.target.value };
                        setForm({ ...form, items });
                      }} className="h-9 rounded-sm mt-1" data-testid={`item-repl-${idx}`} />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    <button className="h-9 px-2 text-muted-foreground hover:text-rose-500" data-testid={`item-remove-${idx}`}
                      onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}>
                      <Trash size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="delivery-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" data-testid="delivery-save" onClick={save}>
                <Printer size={15} className="mr-1.5" />
                {form.id ? "Salva modifiche" : "Registra e stampa modulo"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {view && (
        <Dialog open onOpenChange={() => setView(null)}>
          <DialogContent className="rounded-sm sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="delivery-view-dialog">
            <DialogHeader><DialogTitle className="font-head">
              Consegna n. {view.progressive} — {view.employee_name}
            </DialogTitle></DialogHeader>
            <div className="grid gap-3 sm:grid-cols-3 text-sm">
              {[["Data", itDate(view.date)], ["Luogo", view.place || "—"],
                ["Reparto", view.department || "—"],
                ["Consegnato da", view.delivered_by || "—"],
                ["Stato", view.signed ? `Firmata il ${itDate(view.signed_date)}` : "Da firmare"],
                ["Note", view.notes || "—"]].map(([l, v]) => (
                <div key={l}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</div>
                  <div className="mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            <div className="rounded-sm border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="delivery-view-items">
                <thead className="bg-secondary"><tr>
                  {["Articolo", "Tipo", "Q.tà", "Taglia", "Sostituzione"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(view.items || []).map((i, idx) => (
                    <tr key={`${i.description}-${i.size || ""}-${idx}`} className="border-t border-border">
                      <td className="px-3 py-1.5">{i.description}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{i.kind}</td>
                      <td className="px-3 py-1.5 tabular-nums">{i.quantity}</td>
                      <td className="px-3 py-1.5">{i.size || "—"}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{i.replacement}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setView(null)} data-testid="delivery-view-close">Chiudi</Button>
              <Button className="rounded-sm font-semibold" data-testid="delivery-view-print"
                onClick={() => download(`/safety/deliveries/${view.id}/pdf`, `consegna_dpi_${view.progressive}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                <Printer size={15} className="mr-1.5" /> Stampa modulo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {toDelete && (
        <Dialog open onOpenChange={() => setToDelete(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="delivery-delete-dialog">
            <DialogHeader><DialogTitle className="font-head">Eliminare la consegna?</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Stai per eliminare la <b>consegna n. {toDelete.progressive}</b> di{" "}
              <b>{toDelete.employee_name}</b> del {itDate(toDelete.date)}. I DPI consegnati
              torneranno in giacenza in magazzino. L'operazione non può essere annullata.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setToDelete(null)} data-testid="delivery-delete-cancel">Annulla</Button>
              <Button className="rounded-sm bg-rose-600 font-semibold hover:bg-rose-700" onClick={remove} data-testid="delivery-delete-confirm">
                <Trash size={15} className="mr-1.5" /> Elimina definitivamente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {batch && (
        <Dialog open onOpenChange={() => setBatch(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="batch-print-dialog">
            <DialogHeader><DialogTitle className="font-head">Stampa i moduli della giornata</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Un unico PDF con tutti i moduli di consegna del giorno scelto, uno per pagina, pronti
              da far firmare.
            </p>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Giorno</Label>
              <Input type="date" value={batch.day} data-testid="batch-print-day"
                onChange={(e) => setBatch({ ...batch, day: e.target.value, count: null })}
                className="mt-1 h-9 rounded-sm" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={batch.onlyUnsigned} data-testid="batch-print-only-unsigned"
                onChange={(e) => setBatch({ ...batch, onlyUnsigned: e.target.checked, count: null })} />
              Solo i moduli ancora da firmare
            </label>
            <p className="text-sm" data-testid="batch-print-count">
              {batch.count === null ? "Conteggio in corso..."
                : batch.count === 0 ? "Nessun modulo da stampare per questo giorno"
                : <><b>{batch.count}</b> moduli da stampare</>}
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setBatch(null)} data-testid="batch-print-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" onClick={printBatch} disabled={!batch.count}
                data-testid="batch-print-confirm">
                <Printer size={15} className="mr-1.5" /> Scarica PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PpeDeliveries;
